import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, BookOpen, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Courses() {
  const [cat, setCat] = useState("All");
  const { data, isLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const allCourses = data?.courses || [];
  const cats = ["All", ...Array.from(new Set(allCourses.map((c) => c.category)))];
  const courses = allCourses.filter((c) => cat === "All" || c.category === cat);

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Learn</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Courses</h1>
        <p className="text-muted-foreground mt-1">Interview prep, AML/CTF, placement modules and AI-generated lessons.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`course-filter-${c}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              cat === c ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-secondary"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : courses.length === 0 ? (
        <p className="text-muted-foreground py-12">No courses available yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => (
            <div key={c.id} className="p-6 rounded-md border border-border bg-card flex flex-col hover:-translate-y-1 transition-transform" data-testid={`course-card-${c.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                {c.ai_generated && (
                  <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-primary/10 text-primary">
                    <Sparkles className="w-3 h-3" /> AI
                  </span>
                )}
              </div>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{c.category} · {c.difficulty}</span>
              <h3 className="font-heading font-bold text-lg mb-2">{c.title}</h3>
              <p className="text-sm text-muted-foreground mb-6 flex-1 line-clamp-3">{c.description}</p>
              <Link to={`/courses/${c.id}`}>
                <Button variant="outline" className="w-full rounded-md gap-2" data-testid={`open-course-${c.id}`}>
                  Open Course <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
